<template>
  <div class="be-stay w-100">
    <!-- HEADER -->
    <div class="be-stay__header">
      <div>
        <h2 class="be-stay__title">Choose Your Stay</h2>
        <p class="be-stay__subtitle">Choose the best destination for your travels. Many adventurers also choose this spot.</p>
      </div>
      <div class="be-stay__steps">
        <p class="be-stay__steps-label">How it works</p>
        <div class="be-stay__stepper">
          <div class="be-stay__step be-stay__step--active">
            <span class="be-stay__step-dot">1</span> Book
          </div>
          <span class="be-stay__step-line"></span>
          <div class="be-stay__step">
            <span class="be-stay__step-dot">2</span> Payment
          </div>
          <span class="be-stay__step-line"></span>
          <div class="be-stay__step">
            <span class="be-stay__step-dot">3</span> Complete
          </div>
        </div>
      </div>
    </div>

    <!-- BACKDROP -->
    <transition name="fade">
      <div class="be-stay__backdrop" v-if="isFilterActive" @mousedown="closeAllPopups"></div>
    </transition>

    <!-- TABS -->
    <BeStayTabs :is-elevated="isFilterActive" :is-standalone="!isRoomTabActive" />

    <!-- SEARCH BAR -->
    <CollapseTransition>
    <div v-if="isRoomTabActive" class="be-stay__search" :class="{ 'be-stay__elevated': isFilterActive }">
      <!-- DATE -->
      <div class="be-stay__field be-stay__field--date">
        <div class="be-stay__field-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.6" />
            <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </div>
        <div class="be-stay__field-body">
          <p class="be-stay__field-label">Select date</p>
          <CalendarRoom
            ref="calendar"
            only_for_page="be-v3"
            :opens="$is_mobile_view ? 'center' : 'right'"
            :is_double="!$is_mobile_view"
            wrapper_class="be-stay__calendar"
            @togglePopup="onCalendarToggle"
            @finish="onDate" />
        </div>
      </div>

      <!-- GUEST -->
      <div class="be-stay__field be-stay__field--guest position-relative">
        <div class="be-stay__field-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.6" />
            <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </div>
        <div class="be-stay__field-body cursor-pointer" @click="toggleGuest">
          <p class="be-stay__field-label">Add guest</p>
          <p class="be-stay__field-value">{{ guestSummary }}</p>
        </div>
        <transition name="be-pop-down">
          <div class="be-stay__guest-popover" v-if="showGuest">
            <GuestSelection :defaultData="getBeFilter.guest" @guest-change="onGuest" />
          </div>
        </transition>
      </div>

      <!-- PROMO -->
      <div class="be-stay__field be-stay__field--promo">
        <div class="be-stay__field-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 12a2 2 0 0 1 2-2V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v3a2 2 0 0 1 0 4v3a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3a2 2 0 0 1-2-2Z" stroke="currentColor" stroke-width="1.6" />
            <path d="M9 9h.01M15 15h.01M15 9l-6 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </div>
        <div class="be-stay__field-body">
          <p class="be-stay__field-label">Promo Code</p>
          <input
            type="text"
            class="be-stay__promo-input no-focus-border"
            :placeholder="'Get some discount'"
            v-model="promoCode"
            @focus="onPromoFocus"
            @blur="onPromoBlur"
            @keyup.enter="search" />
        </div>
      </div>

      <!-- SEARCH -->
      <button type="button" class="be-stay__search-btn be-press" @click="search">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
          <path d="m20 20-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
        <span class="ml-2">{{ $t('general.search') }}</span>
      </button>
    </div>
    </CollapseTransition>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import CalendarRoom from '@/components/hotel/be/CalendarRoom.vue';
import GuestSelection from '@/components/hotel/be/GuestSelection.vue';
import BeStayTabs from '@/components/hotel/be/v3/BeStayTabs.vue';
import CollapseTransition from '@/components/general/CollapseTransition.vue';

export default {
  name: 'BeChooseYourStay',
  components: {
    CalendarRoom,
    GuestSelection,
    BeStayTabs,
    CollapseTransition,
  },
  data() {
    return {
      showGuest: false,
      showCalendar: false,
      isPromoFocused: false,
      promoCode: '',
    };
  },
  computed: {
    ...mapGetters(['getBeFilter', 'getMerchant', 'getActiveHotelModule']),
    isRoomTabActive() {
      return this.$isEmpty(this.getActiveHotelModule) || this.getActiveHotelModule.id === 'room';
    },
    guestSummary() {
      const { adult, child, infant } = this.getBeFilter.guest;
      let parts = [`${adult} ${this.$pluralWord(adult, 'Adult')}`];
      if (child > 0) parts.push(`${child} ${this.$pluralWord(child, 'Child')}`);
      if (infant > 0) parts.push(`${infant} ${this.$pluralWord(infant, 'Infant')}`);
      return parts.join(', ');
    },
    isFilterActive() {
      return this.showGuest || this.showCalendar || this.isPromoFocused;
    },
    savedPromoCode() {
      return this.getBeFilter.promo_code || '';
    },
  },
  watch: {
    // the page can change the code itself, e.g. dropping an invalid one
    savedPromoCode(promoCode) {
      this.promoCode = promoCode;
    },
  },
  mounted() {
    this.promoCode = this.savedPromoCode;
  },
  methods: {
    ...mapMutations(['setSpecificBeFilter']),
    onDate(date) {
      this.setSpecificBeFilter([
        { key: 'date', value: { check_in: date.startDate, check_out: date.endDate } },
      ]);
    },
    onGuest(guest) {
      this.setSpecificBeFilter([{ key: 'guest', value: guest }]);
    },
    toggleGuest() {
      this.showGuest = !this.showGuest;
      if (this.showGuest) this.closeCalendar();
    },
    onCalendarToggle(isOpen) {
      this.showCalendar = isOpen;
      if (isOpen) this.showGuest = false;
    },
    onPromoFocus() {
      this.isPromoFocused = true;
      this.closeAllPopups();
    },
    onPromoBlur() {
      this.isPromoFocused = false;
    },
    closeCalendar() {
      if (this.$refs.calendar) this.$refs.calendar.closeCalendar();
    },
    closeAllPopups() {
      this.showGuest = false;
      this.showCalendar = false;
      this.closeCalendar();
    },
    search() {
      this.closeAllPopups();
      this.setSpecificBeFilter([{ key: 'promo_code', value: this.promoCode }]);
      this.$emit('search');
    },
  },
};
</script>

<style scoped>
.be-stay__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}
.be-stay__title {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-weight: 600;
  font-size: 28px;
  color: #1a1a2e;
  margin-bottom: 6px;
}
.be-stay__subtitle {
  color: #888;
  font-size: 15px;
  margin: 0;
  max-width: 540px;
}
.be-stay__steps-label {
  color: #888;
  font-size: 13px;
  margin-bottom: 8px;
}
.be-stay__stepper {
  display: flex;
  align-items: center;
}
.be-stay__step {
  display: flex;
  align-items: center;
  color: #999;
  font-size: 14px;
  font-weight: 400;
}
.be-stay__step--active {
  color: #1a1a2e;
}
.be-stay__step-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #d9d9d9;
  color: #fff;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
}
.be-stay__step--active .be-stay__step-dot {
  background: var(--merchant-primary-color, #79a33a);
}
.be-stay__step-line {
  width: 32px;
  height: 1px;
  background: #ddd;
  margin: 0 10px;
}
/* On mobile the three fields read as one bordered block: the date spans the row and
   guest/promo split the one below it, so the gap is 0 and the seams are the fields'
   own borders. The button keeps its own spacing. */
.be-stay__search {
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 0 0 var(--be-v3-radius, 10px) var(--be-v3-radius, 10px);
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}
.be-stay__backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
}
.be-stay__elevated {
  position: relative;
  z-index: 2001;
}
.be-stay__field {
  display: flex;
  align-items: center;
  border: 1px solid #e4e4e4;
  border-radius: var(--be-v3-radius, 10px);
  padding: 12px 14px;
  min-width: 0;
}
.be-stay__field--date {
  grid-column: 1 / -1;
  border-radius: var(--be-v3-radius, 10px) var(--be-v3-radius, 10px) 0 0;
}
.be-stay__field--guest {
  border-top: 0;
  border-radius: 0 0 0 var(--be-v3-radius, 10px);
}
.be-stay__field--promo {
  border-top: 0;
  border-left: 0;
  border-radius: 0 0 var(--be-v3-radius, 10px) 0;
}
/* dropped on mobile — a 36px badge plus its margin leaves the half-width guest and
   promo fields too little room for their values */
.be-stay__field-icon {
  display: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--merchant-primary-color, #79a33a);
  color: #fff;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}
.be-stay__field-body {
  min-width: 0;
  width: 100%;
}
.be-stay__field-label {
  font-size: 13px;
  color: #888;
  margin: 0 0 2px;
}
.be-stay__field-value {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}
.be-stay__calendar >>> .vue-daterange-picker {
  display: block;
  width: 100%;
  height: auto !important;
}
.be-stay__calendar >>> .form-control.reportrange-text {
  border: none;
  background: transparent;
  height: auto;
  padding: 0;
  text-align: left;
  line-height: 1.4;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.be-stay__promo-input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  padding: 0;
  background: transparent;
}
.be-stay__promo-input:focus,
.be-stay__promo-input:focus-visible {
  border: none;
  outline: none;
  box-shadow: none;
}
.be-stay__promo-input::placeholder {
  color: #bbb;
  font-weight: 400;
}
.be-stay__guest-popover {
  position: absolute;
  top: 100%;
  left: 0;
  /* the guest field is half a row on mobile — the popover still needs the full row */
  width: 200%;
  z-index: 1040;
  background: #fff;
  border: 1px solid #eee;
  border-radius: var(--be-v3-radius, 10px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 12px 16px;
  margin-top: 6px;
}
.be-stay__search-btn {
  grid-column: 1 / -1;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--merchant-button-color, var(--merchant-primary-color, #79a33a));
  color: #fff;
  border: none;
  border-radius: var(--be-v3-button-radius, 10px);
  padding: 14px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
}
@media screen and (min-width: 992px) {
  .be-stay__search {
    grid-template-columns: 1.2fr 1fr 1fr auto;
    gap: 12px;
    align-items: stretch;
  }
  .be-stay__field--date,
  .be-stay__field--guest,
  .be-stay__field--promo {
    grid-column: auto;
    border: 1px solid #e4e4e4;
    border-radius: var(--be-v3-radius, 10px);
  }
  .be-stay__field-icon {
    display: flex;
  }
  .be-stay__guest-popover {
    right: 0;
    width: auto;
  }
  .be-stay__search-btn {
    grid-column: auto;
    margin-top: 0;
    padding: 0 32px;
  }
}
/* Jost (the v3 default content font) reads heavier at 600 than BeV3Sans did at the
   same weight, so these light-chrome fields drop to Regular when no custom font is set. */
.be-v3-content-font-default .be-stay__field-value,
.be-v3-content-font-default .be-stay__calendar >>> .form-control.reportrange-text,
.be-v3-content-font-default .be-stay__promo-input,
.be-v3-content-font-default .be-stay__search-btn {
  font-weight: 400;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeChooseYourStay.vue